import React, { useState } from "react";

/**
 * Determines the image source type based on the URL
 * @param {string} imageUrl - The image URL
 * @returns {'wikipedia' | 'ai-generated' | 'unknown'} The source type
 */
export const getImageSourceType = (imageUrl) => {
  if (!imageUrl) return "unknown";

  if (
    imageUrl.includes("generated-species-images") ||
    imageUrl.includes("generated_species_images") ||
    imageUrl.includes("generated-product-images") ||
    imageUrl.includes("generated_product_images")
  ) {
    return "ai-generated";
  }

  // Check for Wikipedia/Wikimedia images
  if (
    imageUrl.includes("wikipedia.org") ||
    imageUrl.includes("wikimedia.org") ||
    imageUrl.includes("upload.wikimedia.org")
  ) {
    return "wikipedia";
  }

  return "unknown";
};

/**
 * Gets the attribution URL for Wikipedia/Wikimedia images
 * @param {string} imageUrl - The image URL
 * @returns {string | null} The attribution URL
 */
export const getWikipediaAttributionUrl = (imageUrl) => {
  if (!imageUrl) return null;
  const fileName = imageUrl.split("/").pop();
  return `https://commons.wikimedia.org/wiki/File:${fileName}`;
};

/**
 * Image Attribution Component
 * Displays appropriate attribution based on image source type
 */
const ImageAttribution = ({
  imageUrl,
  scientificName,
  itemName,
  itemType = "species",
  className = "",
  position = "absolute right-0",
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const sourceType = getImageSourceType(imageUrl);

  if (sourceType === "unknown" || !imageUrl) {
    return null;
  }

  if (sourceType === "ai-generated") {
    return (
      <div
        className={`text-xs text-gray-500 mt-2 text-right ${position} ${className}`}
      >
        <span
          className="cursor-help underline decoration-dotted relative"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          data-posthog="ai-generated-image-attribution"
          data-posthog-scientific-name={scientificName}
          data-posthog-item-name={itemName || scientificName}
          data-posthog-item-type={itemType}
        >
          AI Generated
          {showTooltip && (
            <div className="absolute bottom-full right-0 mb-2 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg z-50">
              This image was generated by AI based on the listings for this
              {` ${itemType}.`}
            </div>
          )}
        </span>
      </div>
    );
  }

  if (sourceType === "wikipedia") {
    return (
      <div
        className={`text-xs text-gray-500 mt-2 text-right ${position} ${className}`}
      >
        <a
          href={getWikipediaAttributionUrl(imageUrl)}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="underline"
          data-posthog="listing-picture-attribution-link"
          data-posthog-scientific-name={scientificName}
          data-posthog-item-name={itemName || scientificName}
          data-posthog-item-type={itemType}
        >
          Image Source
        </a>
      </div>
    );
  }

  return null;
};

export default ImageAttribution;
